// "Ask Reggie" chat panel for the Analytics section.
//
// Conversation state lives in the rhinoanalyst Redux slice (persisted per
// user+organization by RhinoAnalystDrawer); this component owns only the
// transient UI state: the draft, streaming status, feedback and copy state.
// The full text history is sent on every turn; the service stores nothing.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar, Button, Close, Icon, Scrollbars,
} from 'rhinostyle';
import moment from 'moment-timezone';

import { streamChat, toolLabel } from '../services/RhinoAnalystService';
import RhinoAnalystMarkdown from './RhinoAnalystMarkdown';
import ReggieChart from './ReggieChart';
import ReggieCsv from './ReggieCsv';
import ReggieReport from './ReggieReport';
import * as UserHelpers from '../helpers/UserHelpers';
import askRhinoImage from '../assets/images/rhinoanalyst/ask-rhino.png';
import {
  activateConversation,
  addAttachment,
  appendMessages,
  deleteConversation,
  demoteContentToStatus,
  getActiveConversation,
  getAskReggieState,
  getConversationList,
  newConversation,
  patchMessage,
  patchToolCall,
  setAskReggieView,
  setServerConversationId,
  startConversation,
} from '../reducers/rhinoanalystReducer';

const DEFAULT_SUGGESTIONS = [
  'What stands out in this report?',
  'When do we miss the most calls?',
  'Which hours need more coverage?',
];

const FOLLOW_UPS = {
  get_call_volume: ['Break down by hour', 'Compare ring groups'],
  get_missed_call_rate: ['Break down by hour', 'Which ring group misses the most?'],
  get_call_answer_times: ['Compare ring groups', 'Show by weekday'],
  get_voicemail_stats: ['When do voicemails peak?', 'Compare with missed calls'],
  compare_staffing_to_demand: ['Which hours need more coverage?', 'Show missed calls by hour'],
  get_business_hours: ['Compare staffing to call volume'],
  get_appointment_distribution: ['Show by hour of day', 'Break down by provider'],
  get_message_volume: ['When do patients text us most?', 'Who sends the most messages?'],
  get_conversation_activity: ['Who closes the most conversations?', 'How old is the open queue?', 'Which days do we close the most?'],
};

const ERROR_TEXT = {
  refusal: 'Reggie can\'t help with that request.',
  truncated: 'That answer was cut off. Try a narrower question.',
  rate_limited: 'Reggie is busy right now. Please try again in a moment.',
  too_many_steps: 'That question needed too many lookups. Try splitting it up.',
  upstream: 'Reggie couldn\'t complete that request. Please try again.',
};

// rhinostyle has no thumbs/copy/history icons; tiny inline glyphs keep the panel self-contained.
const GLYPHS = {
  'thumbs-up': 'M2 21h4V9H2v12zm20-11c0-1.1-.9-2-2-2h-6.3l.9-4.6c.1-.5-.1-1-.4-1.3L13 1 7 7.6V21h11c.8 0 1.5-.5 1.8-1.2l3-7c.1-.3.2-.5.2-.8v-2z',
  'thumbs-down': 'M22 3h-4v12h4V3zM2 13c0 1.1.9 2 2 2h6.3l-.9 4.6c-.1.5.1 1 .4 1.3L11 23l6-6.6V3H6c-.8 0-1.5.5-1.8 1.2l-3 7c-.1.3-.2.5-.2.8v1z',
  copy: 'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
  history: 'M13 3a9 9 0 0 0-9 9H1l3.9 3.9L8.8 12H6a7 7 0 1 1 2.1 5l-1.4 1.4A9 9 0 1 0 13 3zm-1 5v5l4.3 2.5.7-1.2-3.5-2.1V8H12z',
  trash: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
};

const Glyph = ({ name }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d={GLYPHS[name]} />
  </svg>
);

Glyph.propTypes = { name: PropTypes.oneOf(Object.keys(GLYPHS)).isRequired };

const newId = (prefix = 'm') => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const questionCount = (c) => c.messages.filter((m) => m.role === 'user').length;

const RhinoAnalystPanel = ({
  onClose, currentUser, context, dateRange, contextLabel,
}) => {
  const dispatch = useDispatch();
  const { view } = useSelector(getAskReggieState);
  const active = useSelector(getActiveConversation);
  const history = useSelector(getConversationList);
  const messages = useMemo(() => (active ? active.messages : []), [active]);

  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  const userAvatarName = useMemo(
    () => UserHelpers.formatAvatarName(currentUser?.firstName, currentUser?.lastName),
    [currentUser],
  );

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el && typeof el.scrollToBottom === 'function') el.scrollToBottom();
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, view, scrollToBottom]);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const send = useCallback(async (text) => {
    const question = (text || '').trim();
    if (!question || isStreaming) return;

    const now = Date.now();
    let conversationId = active?.id;
    if (!conversationId) {
      conversationId = newId('c');
      dispatch(startConversation({ id: conversationId, now }));
    }

    // Each user turn remembers the page it was asked from; each assistant turn
    // remembers the tools behind it. The service renders both into the transcript so
    // the model can tell when the user moved pages and that earlier numbers were checked.
    const userMessage = {
      id: newId(), role: 'user', content: question, page: context?.page || null,
    };
    const assistantId = newId();
    const historyForModel = [...messages, userMessage]
      .filter((m) => m.content)
      .map((m) => {
        const turn = { role: m.role, content: m.content };
        if (m.role === 'user' && m.page) turn.page = m.page;
        if (m.role === 'assistant') {
          const used = (m.toolCalls || []).filter((t) => t.status === 'done').map((t) => t.name);
          if (used.length) turn.tools = Array.from(new Set(used)).slice(0, 12);
        }
        return turn;
      });

    setError(null);
    setDraft('');
    dispatch(appendMessages({
      id: conversationId,
      now,
      messages: [
        userMessage,
        {
          id: assistantId, role: 'assistant', content: '', toolCalls: [], pending: true,
        },
      ],
    }));
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const patch = (p) => dispatch(patchMessage({ id: conversationId, messageId: assistantId, ...p }));

    try {
      await streamChat({
        messages: historyForModel,
        dateRange,
        context,
        conversationId: active?.serverConversationId || null,
        signal: controller.signal,
        onEvent: (event, data) => {
          if (event === 'text_delta') {
            patch({ appendText: data.text });
          } else if (event === 'tool_call_started') {
            dispatch(demoteContentToStatus({ id: conversationId, messageId: assistantId }));
            dispatch(patchToolCall({
              id: conversationId, messageId: assistantId, toolCall: { id: data.id, name: data.name, status: 'running' },
            }));
          } else if (event === 'tool_call_finished') {
            dispatch(patchToolCall({
              id: conversationId, messageId: assistantId, toolCall: { id: data.id, status: data.ok ? 'done' : 'error' },
            }));
          } else if (event === 'display') {
            dispatch(addAttachment({
              id: conversationId,
              messageId: assistantId,
              attachment: { id: data.tool_use_id || newId('a'), kind: data.kind, payload: data.payload },
            }));
          } else if (event === 'done') {
            if (data.conversation_id) {
              dispatch(setServerConversationId({ id: conversationId, serverConversationId: data.conversation_id }));
            }
            patch({ patch: { pending: false, status: undefined } });
          } else if (event === 'error') {
            patch({ patch: { pending: false, error: ERROR_TEXT[data.code] || data.message } });
          }
        },
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        const { status } = err;
        let friendly = ERROR_TEXT.upstream;
        if (status === 403) friendly = 'Ask Reggie isn\'t available for this organization or your role.';
        if (status === 401) friendly = 'Your session has expired. Please sign in again.';
        setError(friendly);
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      patch({ patch: { pending: false } });
    }
  }, [messages, isStreaming, dateRange, context, active, dispatch]);

  const handleNewChat = () => {
    if (abortRef.current) abortRef.current.abort();
    dispatch(newConversation());
    setError(null);
    setDraft('');
    if (textareaRef.current) textareaRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  const handleCopy = async (m) => {
    try {
      await navigator.clipboard.writeText(m.content);
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) { /* clipboard unavailable */ }
  };

  const toggleFeedback = (id, value) => {
    setFeedback((prev) => ({ ...prev, [id]: prev[id] === value ? null : value }));
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant' && !m.pending);
  const suggestions = useMemo(() => {
    if (!lastAssistant) return DEFAULT_SUGGESTIONS;
    const fromTools = (lastAssistant.toolCalls || []).flatMap((t) => FOLLOW_UPS[t.name] || []);
    const unique = Array.from(new Set(fromTools));
    return unique.length ? unique.slice(0, 3) : DEFAULT_SUGGESTIONS.slice(1);
  }, [lastAssistant]);

  // Progress only: which lookups are running right now. Finished sources are not shown.
  const renderProgress = (m) => {
    const running = (m.toolCalls || []).filter((t) => t.status === 'running');
    if (!m.pending || !running.length) return null;
    return (
      <div className="rhinoanalyst__progress">
        {running.map((t) => (
          <span key={t.id} className="rhinoanalyst__progress__item">
            Looking at {toolLabel(t.name).toLowerCase()}…
          </span>
        ))}
      </div>
    );
  };

  const renderMessage = (m) => {
    if (m.role === 'user') {
      return (
        <div key={m.id} className="rhinoanalyst__msg rhinoanalyst__msg--user">
          <Avatar name={userAvatarName} image={currentUser?.profileImageUrl} size="small" type="member" />
          <div className="rhinoanalyst__msg__bubble">{m.content}</div>
        </div>
      );
    }
    const isLast = lastAssistant && lastAssistant.id === m.id;
    return (
      <div key={m.id} className="rhinoanalyst__msg rhinoanalyst__msg--assistant">
        <img className="rhinoanalyst__msg__avatar" src={askRhinoImage} alt="" />
        <div className="rhinoanalyst__msg__bubble">
          {m.content ? <RhinoAnalystMarkdown text={m.content} /> : null}
          {m.pending && !m.content && m.status && (
            <div className="rhinoanalyst__status">{m.status}</div>
          )}
          {(m.attachments || []).map((a) => {
            if (a.kind === 'chart') return <ReggieChart key={a.id} spec={a.payload} />;
            if (a.kind === 'csv') return <ReggieCsv key={a.id} payload={a.payload} />;
            if (a.kind === 'report') return <ReggieReport key={a.id} payload={a.payload} />;
            return null;
          })}
          {m.pending && !m.content && (
            <div className="rhinoanalyst__typing" aria-label="Reggie is thinking"><span /><span /><span /></div>
          )}
          {renderProgress(m)}
          {m.error && <div className="rhinoanalyst__error u-m-t-small">{m.error}</div>}
          {!m.pending && m.content && (
            <div className="rhinoanalyst__msg__footer">
              {isLast ? (
                <div className="rhinoanalyst__suggestions">
                  {suggestions.map((s) => (
                    <button
                      type="button"
                      key={s}
                      className="rhinoanalyst__suggestion"
                      onClick={() => send(s)}
                      disabled={isStreaming}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : <span />}
              <div className="rhinoanalyst__feedback">
                <button
                  type="button"
                  title="Helpful"
                  aria-label="Helpful"
                  className={feedback[m.id] === 'up' ? 'is-active' : ''}
                  onClick={() => toggleFeedback(m.id, 'up')}
                >
                  <Glyph name="thumbs-up" />
                </button>
                <button
                  type="button"
                  title="Not helpful"
                  aria-label="Not helpful"
                  className={feedback[m.id] === 'down' ? 'is-active' : ''}
                  onClick={() => toggleFeedback(m.id, 'down')}
                >
                  <Glyph name="thumbs-down" />
                </button>
                <button
                  type="button"
                  title={copiedId === m.id ? 'Copied' : 'Copy'}
                  aria-label="Copy answer"
                  className={copiedId === m.id ? 'is-active' : ''}
                  onClick={() => handleCopy(m)}
                >
                  <Glyph name="copy" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="rhinoanalyst__history">
      <div className="rhinoanalyst__history__title">Previous chats</div>
      {!history.length && (
        <div className="rhinoanalyst__history__empty">No previous chats yet. Chats are kept on this device for 30 days.</div>
      )}
      {history.map((c) => (
        <div
          key={c.id}
          className={`rhinoanalyst__history__item${active && active.id === c.id ? ' is-active' : ''}`}
        >
          <button
            type="button"
            className="rhinoanalyst__history__open"
            onClick={() => dispatch(activateConversation(c.id))}
            title="Open this chat"
          >
            <span className="rhinoanalyst__history__item__title">{c.title || 'Untitled chat'}</span>
            <span className="rhinoanalyst__history__item__meta">
              {moment(c.updatedAt).fromNow()} &middot; {questionCount(c)} question{questionCount(c) === 1 ? '' : 's'}
            </span>
          </button>
          <button
            type="button"
            className="rhinoanalyst__history__delete"
            onClick={() => dispatch(deleteConversation(c.id))}
            title="Delete this chat"
            aria-label="Delete this chat"
          >
            <Glyph name="trash" />
          </button>
        </div>
      ))}
    </div>
  );

  const showHistory = view === 'history';

  return (
    <div className="rhinoanalyst__wrapper">
      <div className="rhinoanalyst" data-cypress="askReggiePanel">
        <div className="rhinoanalyst__header">
          <img className="rhinoanalyst__header__avatar" src={askRhinoImage} alt="" />
          <div className="rhinoanalyst__header__titles">
            <div className="rhinoanalyst__header__title">Ask Reggie</div>
            <div className="rhinoanalyst__header__subtitle">Your analytics office assistant</div>
          </div>
          <div className="rhinoanalyst__header__actions">
            <button
              type="button"
              className={`rhinoanalyst__header__iconbtn${showHistory ? ' is-active' : ''}`}
              onClick={() => dispatch(setAskReggieView(showHistory ? 'chat' : 'history'))}
              title={showHistory ? 'Back to chat' : 'Previous chats'}
              aria-label={showHistory ? 'Back to chat' : 'Previous chats'}
            >
              <Glyph name="history" />
            </button>
            <Button type="primary" outlined size="small" onClick={handleNewChat} title="Start a new chat">
              <Icon icon="add" /> New chat
            </Button>
            <Close className="app-page__header__action__close" onClick={onClose} />
          </div>
        </div>

        {!showHistory && (
          <div className="rhinoanalyst__context">
            <span className="rhinoanalyst__context__chip" title={contextLabel}>
              <Icon icon="analytics" size="small" />
              {contextLabel}
            </span>
            <span>Using your current filters</span>
          </div>
        )}

        <Scrollbars className="rhinoanalyst__thread" ref={scrollRef}>
          <div className="rhinoanalyst__thread__inner">
            {showHistory ? renderHistory() : (
              <>
                <div className="rhinoanalyst__hero">
                  <img src={askRhinoImage} alt="Reggie, your analytics assistant" />
                </div>
                {!messages.length && (
                  <>
                    <div className="rhinoanalyst__hero__blurb">
                      Hi, I&apos;m Reggie. Ask about call volume, missed calls, staffing, texts, or appointments. Answers use your practice&apos;s aggregated data only.
                    </div>
                    <div className="rhinoanalyst__suggestions u-flex-justify-center">
                      {DEFAULT_SUGGESTIONS.map((s) => (
                        <button type="button" key={s} className="rhinoanalyst__suggestion" onClick={() => send(s)} disabled={isStreaming}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {messages.map(renderMessage)}
                {error && <div className="rhinoanalyst__error">{error}</div>}
              </>
            )}
          </div>
        </Scrollbars>

        {!showHistory && (
          <div className="rhinoanalyst__composer">
            <div className="rhinoanalyst__composer__box">
              <textarea
                ref={textareaRef}
                rows={1}
                value={draft}
                placeholder="Ask about your analytics..."
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
                aria-label="Ask Reggie a question"
              />
              <button
                type="button"
                className="rhinoanalyst__send"
                onClick={() => send(draft)}
                disabled={isStreaming || !draft.trim()}
                aria-label="Send"
                title="Send"
              >
                <Icon icon="send" />
              </button>
            </div>
            <div className="rhinoanalyst__disclaimer">
              <Icon icon="info-circle" size="small" />
              AI insights &middot; Verify against source reports
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

RhinoAnalystPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  context: PropTypes.shape({
    page: PropTypes.string.isRequired,
    filters: PropTypes.objectOf(PropTypes.string),
  }),
  dateRange: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
  contextLabel: PropTypes.string,
};

RhinoAnalystPanel.defaultProps = {
  currentUser: null,
  context: null,
  dateRange: null,
  contextLabel: 'Analytics',
};

export default RhinoAnalystPanel;
